type SoundType = 'move' | 'capture' | 'promotion' | 'error' | 'victory' | 'defeat';

class GameSounds {
  private audioContext: AudioContext | null = null;
  private enabled = true;

  private initializeAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
      return;
    }

    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume();
    }
  }

  private createBuffer(duration: number, render: (time: number) => number): AudioBuffer | null {
    if (!this.audioContext) return null;

    const sampleRate = this.audioContext.sampleRate;
    const buffer = this.audioContext.createBuffer(1, sampleRate * duration, sampleRate);
    const channel = buffer.getChannelData(0);

    for (let i = 0; i < buffer.length; i++) {
      channel[i] = render(i / sampleRate);
    }

    return buffer;
  }

  private playBuffer(buffer: AudioBuffer | null, volume = 0.1) {
    if (!this.enabled || !buffer || !this.audioContext) return;

    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();

    source.buffer = buffer;
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    gainNode.gain.value = volume;
    source.start();
  }

  play(type: SoundType) {
    if (!this.enabled) return;

    this.initializeAudioContext();

    switch (type) {
      case 'move':
        this.playMove();
        return;
      case 'capture':
        this.playCapture();
        return;
      case 'promotion':
        this.playPromotion();
        return;
      case 'victory':
        this.playVictory();
        return;
      case 'defeat':
        this.playDefeat();
        return;
      case 'error':
        this.playError();
        return;
    }
  }

  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  isEnabled() {
    return this.enabled;
  }

  private playMove() {
    const buffer = this.createBuffer(0.1, (time) => (
      Math.sin(2 * Math.PI * 300 * time) * Math.exp(-10 * time)
    ));

    this.playBuffer(buffer, 0.1);
  }

  private playCapture() {
    const buffer = this.createBuffer(0.15, (time) => (
      Math.sin(2 * Math.PI * 100 * time) * Math.exp(-30 * time) +
      Math.sin(2 * Math.PI * 1200 * time) * Math.exp(-50 * time) +
      Math.sin(2 * Math.PI * 600 * time) * Math.exp(-40 * time)
    ));

    this.playBuffer(buffer, 0.2);
  }

  private playPromotion() {
    const notes = [440, 554, 659, 880];
    const buffer = this.createBuffer(0.6, (time) => {
      const noteIndex = Math.floor(time * 8) % notes.length;
      const frequency = notes[noteIndex];
      const envelope = Math.min(time * 10, 1) * Math.exp(-3 * time);
      const tone =
        Math.sin(2 * Math.PI * frequency * time) * 0.5 +
        Math.sin(4 * Math.PI * frequency * time) * 0.25 +
        Math.sin(6 * Math.PI * frequency * time) * 0.125;
      const shimmer = Math.sin(2 * Math.PI * 1200 * time) * 0.1 * envelope;

      return (tone + shimmer) * envelope * 0.5;
    });

    this.playBuffer(buffer, 0.2);
  }

  private playVictory() {
    const notes = [392, 392, 587.33, 783.99, 659.25, 587.33, 523.25, 783.99];
    const noteDuration = 0.25;
    const buffer = this.createBuffer(2, (time) => {
      const noteIndex = Math.floor(time / noteDuration) % notes.length;
      const noteTime = time % noteDuration;
      const frequency = notes[noteIndex];
      const tone =
        Math.sin(2 * Math.PI * frequency * time) * 0.4 +
        Math.sin(4 * Math.PI * frequency * time) * 0.2 +
        Math.sin(6 * Math.PI * frequency * time) * 0.1;
      const envelope = Math.min(noteTime * 10, 1) * Math.exp(-5 * noteTime);

      return tone * envelope;
    });

    this.playBuffer(buffer, 0.15);
  }

  private playDefeat() {
    const buffer = this.createBuffer(2, (time) => {
      const note = 440 * Math.pow(0.9, time * 2);
      const harmonic = note * 1.5;
      const tremolo = 1 + Math.sin(2 * Math.PI * 5 * time) * 0.1;
      const tone = (
        Math.sin(2 * Math.PI * note * time) * 0.5 +
        Math.sin(2 * Math.PI * harmonic * time) * 0.3
      ) * tremolo;

      return tone * Math.exp(-time);
    });

    this.playBuffer(buffer, 0.15);
  }

  private playError() {
    const buffer = this.createBuffer(0.2, (time) => {
      const frequency = time < 0.1 ? 400 : 300;
      return Math.sin(2 * Math.PI * frequency * time) * Math.exp(-5 * time);
    });

    this.playBuffer(buffer);
  }
}

export const gameSounds = new GameSounds();
